import { JSX, ComponentChildren, RefObject } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { normalizePosition } from '../utils/PRIVATE_floatingUtils';
import { RtlSide } from '../UNSAFE_Floating';

// Hooks
import { useAnimation } from '../hooks/UNSAFE_useAnimation';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useBreakpointValues } from '../hooks/UNSAFE_useBreakpointValues';

// Style imports
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { classNames } from '../utils/UNSAFE_classNames';
import { DrawerLayoutStyles } from './themes/DrawerLayoutStyles.css';
import { DrawerLayoutRedwoodTheme } from './themes/redwood/DrawerLayoutTheme';

// Types
type IntrinsicProps = Pick<JSX.HTMLAttributes<HTMLDivElement>, 'children'>;
type Placement = 'start' | 'end' | 'bottom';
type Status = 'unmounted' | 'initial' | 'opening' | 'open' | 'closing';

type OnCloseDetail = {
  placement: Placement;
  reason: 'escapeKey';
};
type OnTransitionEndDetail = {
  placement: Placement;
  value: boolean;
};
type DisplayMode = 'overlay' | 'reflow';
type ResolvedDisplayMode = 'full-overlay' | 'overlay' | 'reflow';
type Visibility = 'hidden' | 'visible';

type Props = IntrinsicProps & {
  /**
   * The content node to be shown within the Start Drawer
   */
  startDrawer?: ComponentChildren;
  /**
   * The content node to be shown within the End Drawer
   */
  endDrawer?: ComponentChildren;
  /**
   * The content node to be shown within the Bottom Drawer
   */
  bottomDrawer?: ComponentChildren;
  /**
   * Specifies whether Start drawer is open.
   */
  isStartOpen?: boolean;
  /**
   * Specifies the display mode of the Start drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  startDisplay?: DisplayMode | undefined;
  /**
   * Specifies whether End drawer is open.
   */
  isEndOpen?: boolean;
  /**
   * Specifies the display mode of the End drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  endDisplay?: DisplayMode | undefined;
  /**
   * Specifies whether Bottom drawer is open.
   */
  isBottomOpen?: boolean;
  /**
   * Specifies the display mode of the Bottom drawer.
   *
   * Supported values are:
   * <p><code>overlay</code>, <code>reflow</code></p>
   * Default is <code>undefined</code>.
   */
  bottomDisplay?: DisplayMode | undefined;
  /**
   * Specifies callback triggered when a user tries to close a Drawer through UI interaction.
   * The parent should listen to this event and close the Drawer. If the parent fails to remove
   * the Popup, then no change will be done in the UI by the component.
   *
   * Supported detail values are:
   * <p><code>placement: start | end | bottom</code></p>
   * <p><code>reason: escapeKey</code></p>
   */
  onClose?: (detail: OnCloseDetail) => void;
  /**
   * Specifies callback triggered after the animation ends.
   *
   * Supported detail values are:
   * <p><code>placement: start | end | bottom</code></p>
   * <p><code>value: boolean</code></p>
   */
  onTransitionEnd?: (detail: OnTransitionEndDetail) => void;
} & TestIdProps;

/**
 * A drawer layout adds expandable side contents (drawers) alongside some primary content.
 *
 * These drawers automatically swap 'reflow' and 'overlay' display mode based on width of the page and can be placed at the 'start', 'end' or 'bottom' edge.
 */
export const DrawerLayout = ({
  children,
  startDrawer,
  endDrawer,
  bottomDrawer,
  isStartOpen = false,
  isEndOpen = false,
  isBottomOpen = false,
  startDisplay,
  endDisplay,
  bottomDisplay,
  onClose,
  onTransitionEnd,
  testId // ...props
}: Props) => {
  // Constants
  const zero = '0px';
  const duration = 250;
  const stringStart = 'start';
  const stringEnd = 'end';
  const stringBottom = 'bottom';
  const stringInitial = 'initial';
  const stringOpening = 'opening';
  const stringOpen = 'open';
  const stringClosing = 'closing';
  const stringUnmounted = 'unmounted';
  const stringFullOverlay = 'full-overlay';
  const stringOverlay = 'overlay';
  const stringReflow = 'reflow';
  const stringHidden = 'hidden';
  const stringVisible = 'visible';

  // Initialize states
  const [startDrawerStatus, setStartDrawerStatus] = useState<Status>(
    isStartOpen ? stringInitial : stringUnmounted
  );
  const [endDrawerStatus, setEndDrawerStatus] = useState<Status>(
    isEndOpen ? stringInitial : stringUnmounted
  );
  const [bottomDrawerStatus, setBottomDrawerStatus] = useState<Status>(
    isBottomOpen ? stringInitial : stringUnmounted
  );
  const [startVisibility, setStartVisibility] = useState<Visibility>(stringHidden);
  const [endVisibility, setEndVisibility] = useState<Visibility>(stringHidden);
  const [bottomVisibility, setBottomVisibility] = useState<Visibility>(stringHidden);

  // Breakpoints:
  // sm: 0-600px (full-overlay)
  // md: 600-1024px (overlay)
  // lg: >1024px (reflow)
  const displayMode: ResolvedDisplayMode = useBreakpointValues({
    sm: 'full-overlay',
    md: 'overlay',
    lg: 'reflow'
  });
  const [viewportDisplayMode, setViewportDisplayMode] = useState<ResolvedDisplayMode>(displayMode);
  useEffect(() => {
    // Size breakpoint crossed
    setViewportDisplayMode(displayMode);
  }, [displayMode]);

  // Refs
  const startDrawerRef = useRef<HTMLDivElement>(null);
  const endDrawerRef = useRef<HTMLDivElement>(null);
  const bottomDrawerRef = useRef<HTMLDivElement>(null);

  const getElementHeight = (element: HTMLDivElement): number => {
    return Math.round(element.offsetHeight);
  };

  const getElementWidth = (element: HTMLDivElement): number => {
    return Math.round(element.getBoundingClientRect().width);
  };

  // Normalizing position
  const { direction } = useUser();
  const isLtr = direction === 'ltr';

  const getNormalizedPlacement = (placement: Placement): RtlSide => {
    return normalizePosition(placement, direction);
  };

  const getTranslateX = (placement: Placement, ref: any): string => {
    return `${getNormalizedPlacement(placement) === 'right' ? '' : '-'}${getElementWidth(
      ref.current as HTMLDivElement
    )}px`;
  };

  const getTranslateY = (ref: any): string => {
    return `${getElementHeight(ref.current as HTMLDivElement)}px`;
  };

  const isBottomPlacement = (placement: Placement): boolean => {
    return placement === stringBottom;
  };

  const isAnimationPending = (status: Status): boolean => {
    return [stringOpening, stringClosing].indexOf(status) > -1;
  };

  const isDrawerAnimationPending = (placement: Placement): boolean => {
    switch (placement) {
      case stringStart:
        return isAnimationPending(startDrawerStatus);
      case stringEnd:
        return isAnimationPending(endDrawerStatus);
      case stringBottom:
        return isAnimationPending(bottomDrawerStatus);
    }
  };

  const isOverlayDisplayMode = (displayMode: ResolvedDisplayMode): boolean => {
    return [stringFullOverlay, stringOverlay].indexOf(displayMode) > -1;
  };

  const getIsDrawerOpen = (placement: Placement): boolean => {
    switch (placement) {
      case stringStart:
        return isStartOpen;
      case stringEnd:
        return isEndOpen;
      case stringBottom:
        return isBottomOpen;
    }
  };

  const getDrawerVisibility = (placement: Placement): Visibility => {
    switch (placement) {
      case stringStart:
        return startVisibility;
      case stringEnd:
        return endVisibility;
      case stringBottom:
        return bottomVisibility;
    }
  };

  const setDrawerVisibility = (placement: Placement, visibility: Visibility): void => {
    switch (placement) {
      case stringStart:
        return setStartVisibility(visibility);
      case stringEnd:
        return setEndVisibility(visibility);
      case stringBottom:
        return setBottomVisibility(visibility);
    }
  };

  const getDrawerRef = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawerRef;
      case stringEnd:
        return endDrawerRef;
      case stringBottom:
        return bottomDrawerRef;
    }
  };

  const getAnimatedNodeRef = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startAnimatedNodeRef;
      case stringEnd:
        return endAnimatedNodeRef;
      case stringBottom:
        return bottomAnimatedNodeRef;
    }
  };

  const getDrawerStatus = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDrawerStatus;
      case stringEnd:
        return endDrawerStatus;
      case stringBottom:
        return bottomDrawerStatus;
    }
  };

  const setDrawerStatus = (placement: Placement, status: Status): void => {
    switch (placement) {
      case stringStart:
        setStartDrawerStatus(status);
        break;
      case stringEnd:
        setEndDrawerStatus(status);
        break;
      case stringBottom:
        setBottomDrawerStatus(status);
        break;
    }
  };

  const resetAnimatedNodeRef = (placement: Placement): void => {
    switch (placement) {
      case stringStart:
        startAnimatedNodeRef(null);
        break;
      case stringEnd:
        endAnimatedNodeRef(null);
        break;
      case stringBottom:
        bottomAnimatedNodeRef(null);
        break;
    }
  };

  const getDrawerDisplayProp = (placement: Placement) => {
    switch (placement) {
      case stringStart:
        return startDisplay;
      case stringEnd:
        return endDisplay;
      case stringBottom:
        return bottomDisplay;
    }
  };

  const getDrawerDisplayMode = (placement: Placement): ResolvedDisplayMode => {
    const displayMode = getDrawerDisplayProp(placement);
    return displayMode || viewportDisplayMode;
  };

  // Animation config
  const getAnimationStates = (placement: Placement, ref: RefObject<HTMLElement>): any => {
    const bottomPlacement: boolean = isBottomPlacement(placement);
    if (getDrawerDisplayMode(placement) === stringReflow) {
      // Reflow display mode
      // Effect: Expand/Collapse
      if (bottomPlacement) {
        return {
          opening: () => {
            return {
              from: {
                maxHeight: zero,
                overflowX: 'hidden'
              },
              to: {
                maxHeight: `${getElementHeight(ref.current as HTMLDivElement)}px`,
                overflowX: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                maxHeight: `${getElementHeight(ref.current as HTMLDivElement)}px`
              },
              to: {
                maxHeight: zero,
                overflowX: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          }
        };
      } else {
        return {
          opening: () => {
            return {
              from: {
                maxWidth: zero,
                overflowY: 'hidden'
              },
              to: {
                maxWidth: `${getElementWidth(ref.current as HTMLDivElement)}px`,
                overflowY: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                maxWidth: `${getElementWidth(ref.current as HTMLDivElement)}px`
              },
              to: {
                maxWidth: zero,
                overflowY: 'hidden'
              },
              options: {
                duration: duration
              }
            };
          }
        };
      }
    } else {
      // FullOverlay or Overlay display mode
      // Effect: Slidein/Slideout
      if (bottomPlacement) {
        return {
          opening: () => {
            return {
              from: {
                translateY: getTranslateY(ref)
              },
              to: {
                translateY: zero
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                translateY: zero
              },
              to: {
                translateY: getTranslateY(ref)
              },
              options: {
                duration: duration
              }
            };
          }
        };
      } else {
        return {
          opening: () => {
            return {
              from: {
                translateX: getTranslateX(placement, ref)
              },
              to: {
                translateX: zero
              },
              options: {
                duration: duration
              }
            };
          },
          closing: () => {
            return {
              from: {
                translateX: zero
              },
              to: {
                translateX: getTranslateX(placement, ref)
              },
              options: {
                duration: duration
              }
            };
          }
        };
      }
    }
  };

  // Custom hook to be shared for each side
  // It can't be a function as a hook can only be called within a custom hook
  function useNodeRef(placement: Placement, ref: RefObject<HTMLElement>) {
    const isDrawerOpen: boolean = getIsDrawerOpen(placement);
    const status = getDrawerStatus(placement);
    return useAnimation<Status, HTMLDivElement>(status, {
      animationStates: getAnimationStates(placement, ref),
      isAnimatedOnMount: true,
      onAnimationEnd: () => {
        if (status === stringOpening) {
          setDrawerStatus(placement, stringOpen);
          onTransitionEnd?.({
            placement: placement,
            value: true
          });
        } else if (!isDrawerOpen) {
          resetAnimatedNodeRef(placement);
          setDrawerVisibility(placement, stringHidden);
          setDrawerStatus(placement, stringUnmounted);
          onTransitionEnd?.({
            placement: placement,
            value: false
          });
        }
      }
    }).nodeRef;
  }

  // Drawer refs
  const startAnimatedNodeRef = useNodeRef(stringStart, startDrawerRef);
  const endAnimatedNodeRef = useNodeRef(stringEnd, endDrawerRef);
  const bottomAnimatedNodeRef = useNodeRef(stringBottom, bottomDrawerRef);

  //testId support
  const testIdProps = useTestId(testId);

  // Custom hook to be shared for each side
  // It can't be a function as a hook can only be called within a custom hook
  function useDrawerStatus(placement: Placement): void {
    const isDrawerOpen = getIsDrawerOpen(placement);
    const drawerStatus = getDrawerStatus(placement);
    useEffect(() => {
      // 1. Ignore status update on initial render
      if (drawerStatus === stringUnmounted && !isDrawerOpen) {
        return;
      }

      // 2. Mount visually hidden component
      // Component gets mounted, but will be visually hidden to avoid flickering
      // Flickering happens because it gets rendered before animation hook repositions
      // it in next render cycle
      if (drawerStatus === stringUnmounted && isDrawerOpen) {
        setDrawerStatus(placement, stringInitial);
        return;
      }

      // 3. Setup animation
      // Component has been mounted.
      // We will kick out animation as node is already present in DOM.
      // To avoid flickering, we still keep it visually hidden until the next render cycle.
      if (drawerStatus === stringInitial && isDrawerOpen) {
        const drawerRef = getDrawerRef(placement);
        const animatedNodeRef = getAnimatedNodeRef(placement);
        animatedNodeRef(drawerRef.current);
        setDrawerStatus(placement, stringOpening);
        return;
      }

      // 4. Unhide component. It becomes visible.
      if (drawerStatus === stringOpening && isDrawerOpen) {
        setDrawerVisibility(placement, stringVisible);
        return;
      }

      // 5. Status 'open' is set in 'onAnimationEnd' handler

      // 6. Closing
      if (!isDrawerOpen) {
        setDrawerStatus(placement, stringClosing);
      }
    }, [isDrawerOpen, drawerStatus, placement]);
  }

  // Drawers' status handler±
  useDrawerStatus(stringStart);
  useDrawerStatus(stringEnd);
  useDrawerStatus(stringBottom);

  // Keydown handlers
  const handleKeyDownStart = (event: Event): void => {
    handleKeyDown(stringStart, event);
  };
  const handleKeyDownEnd = (event: Event): void => {
    handleKeyDown(stringEnd, event);
  };
  const handleKeyDownBottom = (event: Event): void => {
    handleKeyDown(stringBottom, event);
  };

  const handleKeyDown = (placement: Placement, event: any): void => {
    if (event.code === 'Escape') {
      // Handle only in overlay mode
      if (isOverlayDisplayMode(getDrawerDisplayMode(placement))) {
        if (event.code === 'Escape') {
          onClose?.({
            placement,
            reason: 'escapeKey'
          });
        }
      }
    }
  };

  // Styles
  const { classes, styles } = useComponentTheme<never, DrawerLayoutStyles>(
    DrawerLayoutRedwoodTheme
  );

  const getVisibilityClass = (visibility: Visibility) => {
    return visibility === stringHidden ? styles.visibilityHidden : styles.visibilityVisible;
  };

  const getClassNames = (placement: Placement): string => {
    const displayMode: ResolvedDisplayMode = getDrawerDisplayMode(placement);
    const visibility = getDrawerVisibility(placement);
    const visibilityClass = getVisibilityClass(visibility);
    const animationPendingClass = isDrawerAnimationPending(placement) && styles.animatedDrawer;
    switch (placement) {
      case stringStart:
        if (displayMode === stringFullOverlay) {
          return classNames([
            styles.overlayDrawer,
            styles.fullOverlayDrawerSide,
            isLtr && styles.overlayDrawerLeft,
            !isLtr && styles.overlayDrawerRight,
            visibilityClass,
            animationPendingClass
          ]);
        } else if (displayMode === stringOverlay) {
          return classNames([
            styles.overlayDrawer,
            styles.basicOverlayDrawerSide,
            isLtr && styles.overlayDrawerLeft,
            !isLtr && styles.overlayDrawerRight,
            visibilityClass,
            animationPendingClass
          ]);
        } else {
          return classNames([
            styles.reflowDrawer,
            isLtr && styles.reflowDrawerLeft,
            !isLtr && styles.reflowDrawerRight,
            visibilityClass,
            animationPendingClass
          ]);
        }
      case stringEnd:
        if (displayMode === stringFullOverlay) {
          return classNames([
            styles.overlayDrawer,
            styles.fullOverlayDrawerSide,
            isLtr && styles.overlayDrawerRight,
            !isLtr && styles.overlayDrawerLeft,
            visibilityClass,
            animationPendingClass
          ]);
        } else if (displayMode === stringOverlay) {
          return classNames([
            styles.overlayDrawer,
            styles.basicOverlayDrawerSide,
            isLtr && styles.overlayDrawerRight,
            !isLtr && styles.overlayDrawerLeft,
            visibilityClass,
            animationPendingClass
          ]);
        } else {
          return classNames([
            styles.reflowDrawer,
            isLtr && styles.reflowDrawerRight,
            !isLtr && styles.reflowDrawerLeft,
            visibilityClass,
            animationPendingClass
          ]);
        }
      case stringBottom:
        if ([stringOverlay, stringFullOverlay].indexOf(displayMode) > -1) {
          return classNames([styles.overlayDrawer, styles.overlayDrawerBottom, visibilityClass]);
        } else {
          return classNames([
            styles.reflowDrawer,
            styles.reflowDrawerBottom,
            visibilityClass,
            animationPendingClass
          ]);
        }
    }
  };

  const getDrawer = (placement: Placement): any => {
    const isMounted = getDrawerStatus(placement) !== stringUnmounted;

    if (isMounted) {
      switch (placement) {
        case stringStart:
          return (
            <div
              ref={startDrawerRef}
              className={getClassNames(stringStart)}
              tabIndex={isOverlayDisplayMode(getDrawerDisplayMode(stringStart)) ? -1 : undefined}
              onKeyDown={handleKeyDownStart}>
              {startDrawer}
            </div>
          );
        case stringEnd:
          return (
            <div
              ref={endDrawerRef}
              className={getClassNames(stringEnd)}
              tabIndex={isOverlayDisplayMode(getDrawerDisplayMode(stringStart)) ? -1 : undefined}
              onKeyDown={handleKeyDownEnd}>
              {endDrawer}
            </div>
          );
        case stringBottom:
          return (
            <div
              ref={bottomDrawerRef}
              className={getClassNames(stringBottom)}
              tabIndex={isOverlayDisplayMode(getDrawerDisplayMode(stringStart)) ? -1 : undefined}
              onKeyDown={handleKeyDownBottom}>
              {bottomDrawer}
            </div>
          );
      }
    }
    return null;
  };

  return (
    <div className={classes} {...testIdProps}>
      {getDrawer(stringStart)}
      <div className={styles.centerSection}>
        {/*center section*/}
        <div className={styles.mainContent}>
          {/*main section*/}
          {children}
        </div>
        {getDrawer(stringBottom)}
      </div>
      {getDrawer(stringEnd)}
    </div>
  );
};
